/* eslint-disable react-hooks/exhaustive-deps */

/**
 * A React functional component that renders a ticket status tab component.
 * This component utilizes various hooks and Redux to manage state and handle
 * API requests.
 * @returns The rendered ticket status tab component.
 */
import { debounce } from "lodash";
import { getCloudinaryMediaBaseUrl } from "../../../../config/cloudinaryBase";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import TicketStatusTabComponent from "./ticketStatusTabComponent";
import "./ticketStatusTab.css";

import useWindowDimension from "../../../../components/hooks/useWindowDimension";
import { buildStaffEventTicketRequest } from "../../../../helpers/eventClubScope";
import { eventTicketStatus } from "../../../../store/features/event/eventSlice";
import { showToast } from "../../../../store/features/toast/toastSlice";
import Constant from "../../../../util/constant";

const TicketStatusTabContainer = (props) => {
  /**
   * Initializes various state variables and hooks for the order table component.
   * @returns None
   */
  const { t } = useTranslation();
  const eventID = props?.data?._id;
  const eventDetails = props?.data;

  const eventStatusList = useSelector(
    (state) => state?.event?.eventTicketStatusListResponce
  ); // Access the specific property you need
  const tableContainerRef = useRef(null);
  const smarttableref = useRef();
  const dispatch = useDispatch();
  const dimensions = useWindowDimension();
  const pageSizeRef = useRef(15);
  const [loading, setLoading] = useState(false);
  const [breadcrumbHeight, setBreadcrumbHeight] = useState("");
  const [curruntEventDetailPageHeight, setCurruntEventDetailPageHeight] =
    useState("");
  const [showViewTicketModal, setShowViewTicketModal] = useState(false);
  const [ticketData, setTicketData] = useState();
  const [loaderHtml, setLoaderHtmlHeight] = useState("");
  const [placeHolder, setPlaceHolder] = useState(true);
  const totalHomeDeliveryCharge = eventDetails?.deliverycost;

  /**
   * useEffect hook that updates the orders list and sets a placeholder state after a delay.
   * @returns None
   */

  const getList = async () => {
    const req = buildStaffEventTicketRequest(eventID);
    if (!req) return;
    dispatch(eventTicketStatus(req)).then((resultAction) => {
      if (eventTicketStatus.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (eventTicketStatus.rejected.match(resultAction)) {
        setPlaceHolder(false);
      }
    });
  };

  useEffect(() => {
    getList();
  }, [eventID]);
  /**
   * Closes the view ticket modal by setting the state of `showViewTicketModal` to false.
   * @returns None
   */
  const closeViewTicketModal = () => setShowViewTicketModal(false);
  const openviewTicketModal = (e, item) => {
    setTicketData(item);
    setShowViewTicketModal(true);
  };

  /**
   * Clears the search input by programmatically clicking the clear search button.
   * @returns None
   */
  const ClearSearchButton = () => {
    smarttableref.current.querySelectorAll("button")[0].click();
  };

  const paidTicketCount = (eventStatusList || []).reduce((sum, item) => {
    if (item?.orderstatus !== Constant.GENERALKEYS.PAID) return sum;
    const qty = Number(item?.noofticket);
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0);

  /**
   * An array of column objects used for displaying data in a table.
   * Each column object contains a label, key, and additional properties for filtering and sorting.
   * @type {Array}
   */
  const columns = [
    {
      label: <>{t("USER_DETAILS")}</>,
      key: "userid",
      filter: false,
      sorting: false,
    },
    {
      label: (
        <>
          {t("TICKETS")}{" "}
          <span className="thCount">{paidTicketCount}</span>
        </>
      ),
      key: "noofticket",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("TICKET_TYPE")}</>,
      key: "tickettype",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("AMOUNT_PAID")}</>,
      key: "amount",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("CATALOG_TICKET_ADDONS")}</>,
      key: "addonLines",
      filter: false,
      sorting: false,
    },
    // {
    //   label: <>{t("GST")}</>,
    //   key: "gst",
    //   filter: false,
    //   sorting: false,
    // },
    // {
    //   label: <>{t("BASE_AMOUNT")}</>,
    //   key: "baseamount",
    //   filter: false,
    //   sorting: false,
    // },
    // Home delivery column — hidden for now
    // {
    //   label: <>{t("HOME_DELIVERY")}</>,
    //   key: "isdelivery",
    //   filter: false,
    //   sorting: false,
    // },
    {
      label: <>{t("STATUS")}</>,
      key: "orderstatus",
      filter: false,
      sorting: false,
    },
    // Ticket ID column — hidden for now
    // {
    //   label: <>{t("TICKETID")}</>,
    //   key: "ticketid",
    //   filter: false,
    //   sorting: false,
    // },
    {
      label: <>{t("BOOKING_TIMING")}</>,
      key: "orderstatustimestamp",
      filter: false,
      sorting: false,
    },
    {
      label: <></>,
      key: "viewTicket",
      filter: false,
      sorting: false,
    },
  ];

  /**
   * Handles the action of viewing a ticket.
   * @param {Event} e - The event object.
   * @param {Object} item - The ticket item to view.
   * @returns None
   */
  const onViewTicket = (e, item) => {
    setTimeout(() => {
      dispatch(
        showToast({
          show: false,
          message: t("User “Jerome Bell” has been deleted successfully!"),
          type: "success",
        })
      );
      closeViewTicketModal();
    }, 1000);
  };

  /**
   * useEffect hook that updates the heights of various elements on the page
   * when the loading state changes.
   * @param {{boolean}} loading - The loading state of the page.
   * @returns None
   */
  useEffect(() => {
    loading &&
      setLoaderHtmlHeight(
        document.getElementsByClassName("table-responsive")[0].offsetHeight
      );
    setBreadcrumbHeight(
      document.getElementsByClassName("page-title-box")[0].offsetHeight
    );
    setCurruntEventDetailPageHeight(
      document.getElementsByClassName("curruntEventDetailPage")[0].offsetHeight
    );
  }, [loading]);

  /**
   * Loads more items when triggered, with a delay of 2 seconds.
   * @returns None
   */
  const loadMoreItems = () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      pageSizeRef.current += 10;
    }, 2000);
  };

  /**
   * Debounces the handleTableScroll function to prevent it from being called too frequently.
   * When the table container is scrolled, it checks if the scroll position is at the bottom
   * of the container. If it is, and there are more items to load, it calls the loadMoreItems function.
   * @returns None
   */
  const handleTableScroll = debounce(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const isAtBottom =
        tableContainer.scrollTop + tableContainer.clientHeight >=
        tableContainer.scrollHeight;
      if (isAtBottom && eventStatusList?.length > pageSizeRef.current) {
        loadMoreItems();
      }
    }
  }, 200);

  /**
   * Adds an event listener to the table container element to handle scroll events.
   * The event listener is removed when the component is unmounted.
   * @param {{function}} handleTableScroll - The function to handle the table scroll event.
   * @returns None
   */
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleTableScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [handleTableScroll]);

  /**
   * Updates the order data when a checkbox is changed.
   * @param {{object}} e - The event object containing information about the checkbox change.
   * @returns None
   */
  const onCheckChange = (e) => {};

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  return (
    <TicketStatusTabComponent
      showViewTicketModal={showViewTicketModal}
      openviewTicketModal={(e, item) => {
        openviewTicketModal(e, item);
      }}
      smarttableref={smarttableref}
      closeViewTicketModal={() => {
        setShowViewTicketModal(false);
      }}
      onViewTicket={(e, item) => onViewTicket(e, item)}
      columns={columns}
      orderData={eventStatusList}
      clearSearchButton={ClearSearchButton}
      emptyImg={CLOUDINARY_URL + "noticket.png"}
      emptyTitle={t("NO_TICKETS_SOLD_YET")}
      noDataFound={t("NO_DATA_FOUND")}
      noDataTitle={t("NO_DATA_FOUND")}
      noDataDesc={t(
        "YOUR_SEARCH_DID_NOT_MATCH_ANY_USER_NAMES_PLEASE_TRY_SEARCHING_AGAIN"
      )}
      emptyDataDesc={t("STILL_NO_TICKETS_HAVE_BEEN_SOLD")}
      emptyDataDescTow={t("PLEASE_CHECK_AFTER_SOMETIME")}
      noDataAddButtonTitle={t("ADD_USER")}
      loaderHtml={loaderHtml}
      placeHolderShimer={placeHolder}
      itemsPerPage={pageSizeRef.current}
      dimensions={dimensions}
      breadcrumbHeight={breadcrumbHeight}
      curruntEventDetailPageHeight={curruntEventDetailPageHeight}
      loading={loading}
      tableContainerRef={tableContainerRef}
      ticketData={ticketData}
      onCheckChange={onCheckChange}
      eventDetails={eventDetails}
      totalHomeDeliveryCharge={totalHomeDeliveryCharge}
    />
  );
};

export default TicketStatusTabContainer;
